import React, { useState, useCallback } from 'react';
import LetterTemplateSelector from './LetterTemplateSelector';
import LetterEditor from './LetterEditor';
import { letterTemplateService } from '../../services/letterTemplateService';
import type { LetterTemplate, PopulateResult } from '../../types/letterTemplate.types';
import { STANDARD_VARIABLES } from '../../types/letterTemplate.types';

interface LetterExportFlowProps {
  isOpen: boolean;
  onClose: () => void;
  /** Report data to auto-populate variables */
  reportData: Record<string, any>;
}

type FlowStep = 'select' | 'variables' | 'edit';

const LetterExportFlow: React.FC<LetterExportFlowProps> = ({ isOpen, onClose, reportData }) => {
  const [step, setStep] = useState<FlowStep>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<LetterTemplate | null>(null);
  const [populateResult, setPopulateResult] = useState<PopulateResult | null>(null);
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  const [editedHtml, setEditedHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectTemplate = useCallback(async (template: LetterTemplate) => {
    setSelectedTemplate(template);
    setError(null);
    setLoading(true);

    try {
      const result = await letterTemplateService.populateTemplate(template.id, reportData);
      setPopulateResult(result);

      // Always go straight to editor - filled variables are already in the HTML,
      // and any remaining {variable_name} placeholders can be edited inline
      setEditedHtml(result.populatedHtml);
      setStep('edit');
    } catch (err) {
      console.error('Failed to populate template:', err);
      setError('Failed to populate template. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [reportData]);

  const handleApplyCustomVariables = useCallback(() => {
    if (!populateResult) return;

    let html = populateResult.populatedHtml;
    for (const [key, value] of Object.entries(customVariables)) {
      if (value) {
        const regex = new RegExp(`\\{${key}\\}`, 'g');
        html = html.replace(regex, value);
      }
    }

    setEditedHtml(html);
    setStep('edit');
  }, [populateResult, customVariables]);

  const handleBack = useCallback(() => {
    if (step === 'edit' && populateResult?.remainingVariables.length) {
      setStep('variables');
    } else if (step === 'edit' || step === 'variables') {
      setStep('select');
      setSelectedTemplate(null);
      setPopulateResult(null);
    }
  }, [step, populateResult]);

  const handleClose = useCallback(() => {
    setStep('select');
    setSelectedTemplate(null);
    setPopulateResult(null);
    setCustomVariables({});
    setEditedHtml('');
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Step 1: Template selection
  if (step === 'select') {
    return (
      <LetterTemplateSelector
        isOpen={true}
        onClose={handleClose}
        onSelect={handleSelectTemplate}
      />
    );
  }

  // Loading overlay
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" />
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4">
          <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-gray-600 dark:text-gray-400">Populating template...</p>
        </div>
      </div>
    );
  }

  // Step 2: Fill remaining custom variables
  if (step === 'variables') {
    const standardNames = STANDARD_VARIABLES.map((v) => v.name);
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Fill In Variables</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                These variables weren't auto-filled from the report data.
              </p>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm rounded-lg">
              {error}
            </div>
          )}

          {/* Auto-filled summary */}
          {populateResult && Object.keys(populateResult.filledVariables).length > 0 && (
            <div className="px-4 pt-4">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Auto-filled from report</h3>
              <div className="flex flex-wrap gap-1 mb-3">
                {Object.entries(populateResult.filledVariables).map(([key, value]) => (
                  <span key={key} className="text-xs px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" title={value}>
                    {'{' + key + '}'}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {populateResult?.remainingVariables.map((varName) => {
              const stdVar = STANDARD_VARIABLES.find((v) => v.name === varName);
              return (
                <div key={varName}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {stdVar ? stdVar.label : varName}
                    <span className="ml-1 text-xs text-gray-400">{'{' + varName + '}'}</span>
                  </label>
                  {stdVar?.description && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">{stdVar.description}</p>
                  )}
                  <input
                    type="text"
                    value={customVariables[varName] || ''}
                    onChange={(e) => setCustomVariables((prev) => ({ ...prev, [varName]: e.target.value }))}
                    placeholder={`Enter ${stdVar ? stdVar.label.toLowerCase() : varName}...`}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
                  />
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
            <button onClick={handleBack} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
              Back
            </button>
            <button
              onClick={handleApplyCustomVariables}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue to Editor
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: WYSIWYG Editor
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Back">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {selectedTemplate?.title || 'Edit Letter'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Edit the letter content, then export as PDF using the toolbar button.
            </p>
          </div>
        </div>
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Close">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Editor */}
      <div className="flex-1 min-h-0">
        <LetterEditor
          content={editedHtml}
          onChange={setEditedHtml}
          enableExport={true}
          exportFileName={selectedTemplate?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'letter'}
        />
      </div>
    </div>
  );
};

export default LetterExportFlow;
